import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, Camera, Clock, Download, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { useRole } from '@/hooks/useRole';
import { supabase } from '@/lib/supabase';
import { formatDateTimeDZ, formatNumberDZ } from '@/lib/format';

interface BackupItem {
  path: string;
  created_at: string;
  size: number;
  scope: string;
}

export function BackupsPage(): JSX.Element {
  const { role, loading } = useRole();
  const [backups, setBackups] = useState<BackupItem[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [purging, setPurging] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<BackupItem | null>(null);
  const [scopeFilter, setScopeFilter] = useState<string>('');

  const refresh = async (): Promise<void> => {
    setRefreshing(true);
    try {
      const all: BackupItem[] = [];
      const traverse = async (prefix: string): Promise<void> => {
        const { data, error } = await supabase.storage.from('backups').list(prefix, {
          sortBy: { column: 'created_at', order: 'desc' },
          limit: 200,
        });
        if (error || !data) return;
        for (const f of data) {
          const fullPath = prefix ? `${prefix}/${f.name}` : f.name;
          if (f.id) {
            all.push({
              path: fullPath,
              created_at: f.created_at ?? '',
              size: (f.metadata?.size as number | undefined) ?? 0,
              scope: prefix || 'racine',
            });
          } else {
            await traverse(fullPath);
          }
        }
      };
      await traverse('');
      setBackups(all);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (role === 'super_admin') void refresh();
  }, [role]);

  // Only redirect once role is actually known — never gate on `loading`,
  // which can stall and trap the page on a spinner forever.
  if (!loading && role !== null && role !== 'super_admin') {
    return <Navigate to="/parametres" replace />;
  }

  const filtered = scopeFilter ? backups.filter((b) => b.scope === scopeFilter) : backups;

  const totalSize = backups.reduce((acc, b) => acc + b.size, 0);
  const scopes = Array.from(new Set(backups.map((b) => b.scope))).sort();

  const downloadBackup = async (path: string): Promise<void> => {
    const { data, error } = await supabase.storage.from('backups').createSignedUrl(path, 600);
    if (error || !data) {
      toast.error('Lien indisponible : ' + (error?.message ?? '?'));
      return;
    }
    window.open(data.signedUrl, '_blank');
  };

  const deleteBackup = async (path: string): Promise<void> => {
    const { error } = await supabase.storage.from('backups').remove([path]);
    if (error) {
      toast.error('Erreur : ' + error.message);
    } else {
      toast.success('Backup supprimé');
      void refresh();
    }
  };

  const purgeOld = async (): Promise<void> => {
    setPurging(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-purge-backups', {
        body: { retention_days: 30 },
      });
      if (error || (data && (data as { error?: string }).error)) {
        toast.error('Erreur : ' + (error?.message ?? (data as { error?: string }).error));
        return;
      }
      const r = data as {
        candidates: number;
        deleted: number;
        bytes_to_free: number;
      };
      toast.success(
        `Purge OK · ${r.deleted}/${r.candidates} fichier(s) supprimé(s) · ${formatNumberDZ(
          Math.round(r.bytes_to_free / 1024),
        )} kB libérés`,
      );
      void refresh();
    } finally {
      setPurging(false);
    }
  };

  const dryRun = async (): Promise<void> => {
    const { data, error } = await supabase.functions.invoke('admin-purge-backups', {
      body: { retention_days: 30, dry_run: true },
    });
    if (error) {
      toast.error('Erreur : ' + error.message);
      return;
    }
    const r = data as { candidates: number; bytes_to_free: number };
    toast.info(
      `${r.candidates} fichier(s) candidat(s) à la purge · ${formatNumberDZ(
        Math.round(r.bytes_to_free / 1024),
      )} kB seraient libérés`,
    );
  };

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Backups & snapshots"
        subtitle="Gestion des sauvegardes générées par les actions admin et les snapshots manuels."
        actions={
          <Button variant="outline" onClick={() => void refresh()} disabled={refreshing}>
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Rafraîchir
          </Button>
        }
      />

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-md border border-border bg-card p-3">
          <div className="text-xs uppercase text-muted-foreground">Total fichiers</div>
          <div className="num text-2xl font-semibold">{formatNumberDZ(backups.length)}</div>
        </div>
        <div className="rounded-md border border-border bg-card p-3">
          <div className="text-xs uppercase text-muted-foreground">Espace utilisé</div>
          <div className="num text-2xl font-semibold">
            {formatNumberDZ(Math.round(totalSize / 1024))} kB
          </div>
        </div>
        <div className="rounded-md border border-border bg-card p-3">
          <div className="text-xs uppercase text-muted-foreground">Scopes</div>
          <div className="text-sm">{scopes.length} catégorie(s)</div>
        </div>
      </div>

      <div className="rounded-md border border-warning/30 bg-warning/10 p-3 text-xs">
        <div className="flex items-start gap-2">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <div className="space-y-1">
            <p className="font-semibold text-warning">
              Cron purge programmé (tous les jours 02:00 UTC, retention 30j)
            </p>
            <p className="text-muted-foreground">
              Le job <code className="font-mono">purge-backups-daily</code> est créé via{' '}
              <code>pg_cron</code>. Pour l&apos;activer, l&apos;administrateur doit configurer
              manuellement les paramètres DB via le Dashboard Supabase &gt; SQL Editor :
            </p>
            <pre className="mt-1 overflow-x-auto rounded bg-muted/40 p-2 font-mono text-[10px]">
              {`alter database postgres set "app.settings.supabase_url" = 'https://kiofhkesuxambcxzthxy.supabase.co';
alter database postgres set "app.settings.service_role_key" = '<SERVICE_ROLE_KEY>';`}
            </pre>
            <p className="text-muted-foreground">
              En attendant, déclenche manuellement la purge ci-dessous.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={() => void dryRun()}>
          <AlertTriangle className="h-4 w-4" /> Aperçu purge (dry-run)
        </Button>
        <Button onClick={() => void purgeOld()} disabled={purging}>
          {purging && <Loader2 className="h-4 w-4 animate-spin" />}
          <Trash2 className="h-4 w-4" /> Purger maintenant (&gt; 30 j)
        </Button>
        <select
          value={scopeFilter}
          onChange={(e) => setScopeFilter(e.target.value)}
          className="ml-auto h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">— Tous les scopes —</option>
          {scopes.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Chemin</th>
              <th className="px-3 py-2 text-left">Scope</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-right">Taille (kB)</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-8 text-center text-xs text-muted-foreground">
                  Aucun backup pour ce filtre.
                </td>
              </tr>
            ) : (
              filtered.map((b) => {
                const isSnapshot = b.path.startsWith('snapshots/');
                return (
                  <tr
                    key={b.path}
                    className="border-b border-border last:border-0 hover:bg-muted/20"
                  >
                    <td className="px-3 py-2 font-mono text-xs">
                      {isSnapshot && <Camera className="mr-1 inline h-3 w-3 text-primary" />}
                      {b.path}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={isSnapshot ? 'default' : 'outline'}>{b.scope}</Badge>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {b.created_at ? formatDateTimeDZ(b.created_at) : '—'}
                    </td>
                    <td className="num px-3 py-2 text-right text-xs">
                      {formatNumberDZ(Math.round(b.size / 1024))}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void downloadBackup(b.path)}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setConfirmDelete(b)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title="Supprimer ce backup ?"
        description={`Le fichier ${confirmDelete?.path} sera retiré du Storage. Action irréversible.`}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await deleteBackup(confirmDelete.path);
          setConfirmDelete(null);
        }}
      />
    </div>
  );
}
